import dayjs from "dayjs";

const parseNull = data => {
  return data ? data : null;
};

const roleNameFilter = name => {
  switch (name) {
    case "School Admin":
      return "ผู้ดูแลโรงเรียน";
    case "Director":
      return "ผู้บริหาร";
    case "Super Admin":
      return "ผู้ดูแลระบบ";
    case "Teacher":
      return "คุณครู";
    case "Assistant Teacher":
      return "ครูพี่เลี้ยง";
    default:
      return "ผู้ใช้งานทั่วไป";
  }
};

class SchoolUser {
  constructor(json) {
    if (!json) return null;
    this.id = parseNull(json["_id"]);
    this.email = parseNull(json["email"]);
    this.prefix = parseNull(json["prefix"]);
    this.firstname = parseNull(json["firstname"]);
    this.lastname = parseNull(json["lastname"]);
    this.imageUrl = parseNull(json["imageUrl"]);
    this.phone = parseNull(json["phone"]);
    this.line = parseNull(json["line"]);
    this.role = parseNull(json["role"]);
    this.signer = parseNull(json["signer"]);
    this.signUrl = parseNull(json["signUrl"]);
    this.createAt = parseNull(json["createAt"]);
    this.school = parseNull(json["school"]);
    this.roleName = this._roleName();
  }

  _roleName() {
    let names = this.role.map(name => {
      return roleNameFilter(name);
    });

    return names.join(", ");
  }

  fullname() {
    return `${this.prefix} ${this.firstname} ${this.lastname}`;
  }

  isTeacher() {
    return this.role.includes("Teacher");
  }

  isDirector() {
    return this.role.includes("Director");
  }

  isClassTeacher(classroom) {
    const { currentTeacher } = classroom;
    if (!currentTeacher) {
      return false;
    }
    return this.id === currentTeacher._id;
  }
}

class Address {
  constructor(json) {
    this.address = parseNull(json["address"]);
    this.subdistrict = parseNull(json["subdistrict"]);
    this.district = parseNull(json["district"]);
    this.province = parseNull(json["province"]);
    this.zipcode = parseNull(json["zipcode"]);
  }
}

class People {
  constructor(json) {
    this.prefix = parseNull(json["prefix"]);
    this.firstname = parseNull(json["firstname"]);
    this.lastname = parseNull(json["lastname"]);
    this.gender = parseNull(json["gender"]);
    this.phone = parseNull(json["phone"]);
    this.occupation = parseNull(json["occupation"]);
    this.position = parseNull(json["position"]);
    this.drug = null;
    this.trutest = null;
  }

  fullname() {
    return `${this.prefix}${this.firstname} ${this.lastname}`;
  }

  phoneNo() {
    return this.phone ? this.phone : "ไม่ระบุ";
  }

  job() {
    return this.occupation ? this.occupation : "-";
  }

  relation() {
    return this.position ? this.position : "-";
  }
}

class ImportCrew {
  constructor(json) {
    this.prefix = parseNull(json["prefix"]);
    this.firstname = parseNull(json["firstname"]);
    this.lastname = parseNull(json["lastname"]);
    this.email = parseNull(json["email"]);
    this.phone = parseNull(json["phone"]);
    this.password = parseNull(json["password"]);
    this.role = parseNull(json["role"]);
  }
}

class ImportStudent {
  constructor(json) {
    this.firstname = parseNull(json["firstname"]);
    this.lastname = parseNull(json["lastname"]);
    this.nickname = parseNull(json["nickname"]);
    this.gender = parseNull(json["gender"]);
    this.studentNo = parseNull(json["studentNo"]);
    this.idCard = parseNull(json["idCard"]);
    // this.birthdate = parseNull(json["birthdate"]);
    this.birthdate = dayjs(json["birthdate"]).format("YYYY-MM-DD");
    this.phone = parseNull(json["phone"]);
    this.studyClass = parseNull(json["studyClass"]);

    this.classroom = {
      room: parseNull(json["classroom"]),
      no: parseNull(json["classroom_no"]),
      title: parseNull(json["classroom_title"])
    };
    this.address = new Address(json);
    this.healthProfile = {
      drugAllergies: [],
      foodAllergies: [],
      congenitalDiseases: []
    };

    let father = new People({
      prefix: json["father_prefix"],
      firstname: json["father_firstname"],
      lastname: json["father_lastname"],
      gender: json["father_gender"],
      phone: json["father_phone"],
      occupation: json["father_occupation"],
      position: "บิดา",
      drug: null,
      trutest: null
    });

    let mother = new People({
      prefix: json["mother_prefix"],
      firstname: json["mother_firstname"],
      lastname: json["mother_lastname"],
      gender: json["mother_gender"],
      phone: json["mother_phone"],
      occupation: json["mother_occupation"],
      position: "มารดา",
      drug: null,
      trutest: null
    });

    let parent = new People({
      prefix: json["parent_prefix"],
      firstname: json["parent_firstname"],
      lastname: json["parent_lastname"],
      gender: json["parent_gender"],
      phone: json["parent_phone"],
      occupation: json["parent_occupation"],
      position: json["parent_position"],
      drug: null,
      trutest: null
    });

    let parents = [father, mother, parent].filter(people => people.firstname);

    this.family = {
      parents,
      siblings: {
        blood: [],
        step: []
      },
      familyStatus: "ไม่ระบุ",
      parentRelationship: "ไม่ระบุ"
    };
  }

  isBeforeThree() {
    return this.studyClass === "ปฐมวัย (อายุต่ำกว่า 3 ปี)";
  }

  fullname() {
    return `${this.prefix()}${this.firstname} ${this.lastname}`;
  }

  prefix() {
    return this.gender === "ชาย" ? "เด็กชาย" : "เด็กหญิง";
  }

  age() {
    let d1 = dayjs(this.birthdate);
    let d2 = dayjs();
    let years = d2.diff(d1, "year");
    let months = d2.diff(d1, "month") % 12;
    return { years, months };
  }

  ageString() {
    let d1 = dayjs(this.birthdate);
    let d2 = dayjs();
    let years = d2.diff(d1, "year");
    let months = d2.diff(d1, "month") % 12;
    return `${years} ปี ${months} เดือน`;
  }

  father() {
    let family = this.family;
    if (!family) {
      return null;
    }

    let parents = this.family.parents;
    if (!parents) {
      return null;
    }

    if (parents.length === 0) {
      return null;
    }

    let filtered = parents.filter(parent => parent.position === "บิดา");

    if (filtered.length === 0) {
      return null;
    }

    return new People(filtered[0]);
  }

  mother() {
    let family = this.family;
    if (!family) {
      return null;
    }

    let parents = this.family.parents;
    if (!parents) {
      return null;
    }

    if (parents.length === 0) {
      return null;
    }

    let filtered = parents.filter(parent => parent.position === "มารดา");

    if (filtered.length === 0) {
      return null;
    }

    return new People(filtered[0]);
  }

  otherParent() {
    let family = this.family;
    if (!family) {
      return null;
    }

    let parents = this.family.parents;
    if (!parents) {
      return null;
    }

    if (parents.length === 0) {
      return null;
    }

    return new People(parents[0]);
  }
}

class NewImportedStudent {
  constructor(json) {
    this.firstname = parseNull(json["firstname"]);
    this.lastname = parseNull(json["lastname"]);
    this.nickname = parseNull(json["nickname"]);
    this.gender = parseNull(json["gender"]);
    this.studentNo = parseNull(json["studentNo"]);
    this.idCard = parseNull(json["idCard"]);
    this.birthdate = parseNull(json["birthdate"]);
    this.phone = parseNull(json["phone"]);
    this.studyClass = parseNull(json["studyClass"]);
    this.address = new Address(json);
    this.healthProfile = {
      drugAllergies: [],
      foodAllergies: [],
      congenitalDiseases: []
    };

    let father = new People({
      prefix: json["father_prefix"],
      firstname: json["father_firstname"],
      lastname: json["father_lastname"],
      gender: json["father_gender"],
      phone: json["father_phone"],
      occupation: json["father_occupation"],
      position: "บิดา",
      drug: null,
      trutest: null
    });

    let mother = new People({
      prefix: json["mother_prefix"],
      firstname: json["mother_firstname"],
      lastname: json["mother_lastname"],
      gender: json["mother_gender"],
      phone: json["mother_phone"],
      occupation: json["mother_occupation"],
      position: "มารดา",
      drug: null,
      trutest: null
    });

    let parent = new People({
      prefix: json["parent_prefix"],
      firstname: json["parent_firstname"],
      lastname: json["parent_lastname"],
      gender: json["parent_gender"],
      phone: json["parent_phone"],
      occupation: json["parent_occupation"],
      position: json["parent_position"],
      drug: null,
      trutest: null
    });

    let parents = [father, mother, parent].filter(people => people.firstname);

    this.family = {
      parents,
      siblings: {
        blood: [],
        step: []
      },
      familyStatus: "ไม่ระบุ",
      parentRelationship: "ไม่ระบุ"
    };
  }

  isBeforeThree() {
    return this.studyClass === "ปฐมวัย (อายุต่ำกว่า 3 ปี)";
  }

  fullname() {
    return `${this.prefix()}${this.firstname} ${this.lastname}`;
  }

  prefix() {
    return this.gender === "ชาย" ? "เด็กชาย" : "เด็กหญิง";
  }

  age() {
    let d1 = dayjs(this.birthdate);
    let d2 = dayjs();
    let years = d2.diff(d1, "year");
    let months = d2.diff(d1, "month") % 12;
    return { years, months };
  }

  ageString() {
    let d1 = dayjs(this.birthdate);
    let d2 = dayjs();
    let years = d2.diff(d1, "year");
    let months = d2.diff(d1, "month") % 12;
    return `${years} ปี ${months} เดือน`;
  }

  father() {
    let family = this.family;
    if (!family) {
      return null;
    }

    let parents = this.family.parents;
    if (!parents) {
      return null;
    }

    if (parents.length === 0) {
      return null;
    }

    let filtered = parents.filter(parent => parent.position === "บิดา");

    if (filtered.length === 0) {
      return null;
    }

    return new People(filtered[0]);
  }

  mother() {
    let family = this.family;
    if (!family) {
      return null;
    }

    let parents = this.family.parents;
    if (!parents) {
      return null;
    }

    if (parents.length === 0) {
      return null;
    }

    let filtered = parents.filter(parent => parent.position === "มารดา");

    if (filtered.length === 0) {
      return null;
    }

    return new People(filtered[0]);
  }

  otherParent() {
    let family = this.family;
    if (!family) {
      return null;
    }

    let parents = this.family.parents;
    if (!parents) {
      return null;
    }

    if (parents.length === 0) {
      return null;
    }

    return new People(parents[0]);
  }
}

class Student {
  constructor({
    id,
    idCard,
    firstname,
    lastname,
    nickname,
    gender,
    classroom,
    studyClass,
    studentNo,
    school,
    status,
    birthdate,
    family,
    address,
    historyClassrooms,
    healthProfile
  }) {
    this.id = id;
    this.idCard = idCard;
    this.firstname = firstname;
    this.lastname = lastname;
    this.nickname = nickname;
    this.gender = gender;
    this.classroom = classroom;
    this.studyClass = studyClass;
    this.studentNo = studentNo;
    this.status = status;
    this.school = school;
    this.birthdate = birthdate;
    this.family = family;
    this.address = address;
    this.historyClassrooms = historyClassrooms;
    this.healthProfile = healthProfile;
  }

  isBeforeThree() {
    return this.studyClass === "ปฐมวัย (อายุต่ำกว่า 3 ปี)";
  }

  fullname() {
    return `${this.prefix()}${this.firstname} ${this.lastname}`;
  }

  prefix() {
    return this.gender === "ชาย" ? "เด็กชาย" : "เด็กหญิง";
  }

  age() {
    let d1 = dayjs(this.birthdate);
    let d2 = dayjs();
    let years = d2.diff(d1, "year");
    let months = d2.diff(d1, "month") % 12;
    return { years, months };
  }

  ageString() {
    let d1 = dayjs(this.birthdate);
    let d2 = dayjs();
    let years = d2.diff(d1, "year");
    let months = d2.diff(d1, "month") % 12;
    return `${years} ปี ${months} เดือน`;
  }

  father() {
    let family = this.family;
    if (!family) {
      return null;
    }

    let parents = this.family.parents;
    if (!parents) {
      return null;
    }

    if (parents.length === 0) {
      return null;
    }

    let filtered = parents.filter(parent => parent.position === "บิดา");

    if (filtered.length === 0) {
      return null;
    }

    return new People(filtered[0]);
  }

  mother() {
    let family = this.family;
    if (!family) {
      return null;
    }

    let parents = this.family.parents;
    if (!parents) {
      return null;
    }

    if (parents.length === 0) {
      return null;
    }

    let filtered = parents.filter(parent => parent.position === "มารดา");

    if (filtered.length === 0) {
      return null;
    }

    return new People(filtered[0]);
  }

  otherParent() {
    let family = this.family;
    if (!family) {
      return null;
    }

    let parents = this.family.parents;
    if (!parents) {
      return null;
    }

    if (parents.length === 0) {
      return null;
    }

    return new People(parents[0]);
  }

  recentAddress() {
    if (!this.address) {
      return null;
    }

    if (this.address.length == 0) {
      return null;
    }

    return this.address[0];
  }

  getStudyClassIn(schoolYear) {
    let selectedClassroom = this.historyClassrooms.find(classroom => {
      return classroom.schoolYear === schoolYear;
    });

    return selectedClassroom.studyClass;
  }

  getHistoryClassroomIn(schoolYear) {
    let histories = this.historyClassrooms.filter(history => {
      return (
        history.schoolYear === schoolYear &&
        !history.status.includes(["ย้่ายห้อง", "ออก"])
      );
    });
    if (histories.length === 0) return null;

    return histories[histories.length - 1];
  }

  isShowInClassroom(classroomId) {
    const move = this.historyClassrooms.find(history => {
      return (
        ["ออก", "ย้ายห้อง"].includes(history.status) &&
        history.room === classroomId
      );
    });

    if (!move) {
      return true;
    }
    return false;
  }

  previousClassroom() {
    if (this.historyClassrooms.length == 0) return null;
    const lastIndex = this.historyClassrooms.length - 1;
    return this.historyClassrooms[lastIndex];
  }
}

const prefixNames = [
  "นาย",
  "นาง",
  "นางสาว",
  "พลตำรวจเอก",
  "พลตำรวจเอก หญิง",
  "พลตำรวจโท",
  "พลตำรวจโท หญิง",
  "พลตำรวจตรี",
  "พลตำรวจตรี หญิง",
  "พันตำรวจเอก",
  "พันตำรวจเอก หญิง",
  "พันตำรวจเอกพิเศษ",
  "พันตำรวจเอกพิเศษ หญิง",
  "พันตำรวจโท",
  "พันตำรวจโท หญิง",
  "พันตำรวจตรี",
  "พันตำรวจตรี หญิง",
  "ร้อยตำรวจเอก",
  "ร้อยตำรวจเอก หญิง",
  "ร้อยตำรวจโท",
  "ร้อยตำรวจโท หญิง",
  "ร้อยตำรวจตรี",
  "ร้อยตำรวจตรี หญิง",
  "นายดาบตำรวจ",
  "ดาบตำรวจหญิง",
  "สิบตำรวจเอก",
  "สิบตำรวจเอก หญิง",
  "สิบตำรวจโท",
  "สิบตำรวจโท หญิง",
  "สิบตำรวจตรี",
  "สิบตำรวจตรี หญิง",
  "จ่าสิบตำรวจ",
  "จ่าสิบตำรวจ หญิง",
  "พลตำรวจ",
  "พลตำรวจ หญิง",
  "พลเอก",
  "พลเอก หญิง",
  "พลโท",
  "พลโท หญิง",
  "พลตรี",
  "พลตรี หญิง",
  "พันเอก",
  "พันเอก หญิง",
  "พันเอกพิเศษ",
  "พันเอกพิเศษ หญิง",
  "พันโท",
  "พันโท หญิง",
  "พันตรี",
  "พันตรี หญิง",
  "ร้อยเอก",
  "ร้อยเอก หญิง",
  "ร้อยโท",
  "ร้อยโท หญิง",
  "ร้อยตรี",
  "ร้อยตรี หญิง",
  "สิบเอก",
  "สิบเอก หญิง",
  "สิบโท",
  "สิบโท หญิง",
  "สิบตรี",
  "สิบตรี หญิง",
  "จ่าสิบเอก",
  "จ่าสิบเอก หญิง",
  "จ่าสิบโท",
  "จ่าสิบโท หญิง",
  "จ่าสิบตรี",
  "จ่าสิบตรี หญิง",
  "พลทหารบก",
  "ว่าที่ พ.ต.",
  "ว่าที่ พ.ต. หญิง",
  "ว่าที่ ร.อ.",
  "ว่าที่ ร.อ. หญิง",
  "ว่าที่ ร.ท.",
  "ว่าที่ ร.ท. หญิง",
  "ว่าที่ ร.ต.",
  "ว่าที่ ร.ต. หญิง",
  "พลเรือเอก",
  "พลเรือเอก หญิง",
  "พลเรือโท",
  "พลเรือโท หญิง",
  "พลเรือตรี",
  "พลเรือตรี หญิง",
  "นาวาเอก",
  "นาวาเอก หญิง",
  "นาวาเอกพิเศษ",
  "นาวาเอกพิเศษ หญิง",
  "นาวาโท",
  "นาวาโท หญิง",
  "นาวาตรี",
  "นาวาตรี หญิง",
  "เรือเอก",
  "เรือเอก หญิง",
  "เรือโท",
  "เรือโท หญิง",
  "เรือตรี",
  "เรือตรี หญิง",
  "พันจ่าเอก",
  "พันจ่าเอก หญิง",
  "พันจ่าโท",
  "พันจ่าโท หญิง",
  "พันจ่าตรี",
  "พันจ่าตรี หญิง",
  "จ่าเอก",
  "จ่าเอก หญิง",
  "จ่าโท",
  "จ่าโท หญิง",
  "จ่าตรี",
  "จ่าตรี หญิง",
  "พลทหารเรือ",
  "พลอากาศเอก",
  "พลอากาศเอก หญิง",
  "พลอากาศโท",
  "พลอากาศโท หญิง",
  "พลอากาศตรี",
  "พลอากาศตรี หญิง",
  "นาวาอากาศเอก",
  "นาวาอากาศเอก หญิง",
  "นาวาอากาศเอกพิเศษ",
  "นาวาอากาศเอกพิเศษ หญิง",
  "นาวาอากาศโท",
  "นาวาอากาศโท หญิง",
  "นาวาอากาศตรี",
  "นาวาอากาศตรี หญิง",
  "เรืออากาศเอก",
  "เรืออากาศเอก หญิง",
  "เรืออากาศโท",
  "เรืออากาศโท หญิง",
  "เรืออากาศตรี",
  "เรืออากาศตรี หญิง",
  "พันจ่าอากาศเอก",
  "พันจ่าอากาศเอก หญิง",
  "พันจ่าอากาศโท",
  "พันจ่าอากาศโท หญิง",
  "พันจ่าอากาศตรี",
  "พันจ่าอากาศตรี หญิง",
  "จ่าอากาศเอก",
  "จ่าอากาศเอก หญิง",
  "จ่าอากาศโท",
  "จ่าอากาศโท หญิง",
  "จ่าอากาศตรี",
  "จ่าอากาศตรี หญิง",
  "พลทหารอากาศ",
  "หม่อม",
  "หม่อมเจ้า",
  "หม่อมราชวงศ์",
  "หม่อมหลวง",
  "ดร.",
  "ศ.ดร.",
  "ศ.",
  "ผศ.ดร.",
  "ผศ.",
  "รศ.ดร.",
  "รศ.",
  "นพ.",
  "แพทย์หญิง",
  "สัตวแพทย์",
  "สพญ.",
  "ทพ.",
  "ทพญ.",
  "เภสัชกร",
  "ภกญ.",
  "พระ",
  "พระครู",
  "พระมหา",
  "พระสมุห์",
  "พระอธิการ",
  "สามเณร",
  "แม่ชี",
  "บาทหลวง",
  "MR",
  "MRS",
  "MS",
  "MISS",
  "Dr."
];

export {
  People,
  Student,
  ImportStudent,
  NewImportedStudent,
  ImportCrew,
  SchoolUser,
  prefixNames
};
